import { handleAsyncStatus } from 'affiny-core/modules/asynchronousState/saga';
import { EAsyncEventTypes } from 'affiny-core/modules/asynchronousState/types';
import {
  selectMatchListCurrentPage,
  selectMatchListTotal,
  selectSortMatchListType,
} from 'affiny-core/redux/selectors/matchesSelectors';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { getMatchList } from '../api';
import {
  DEFAULT_MATCHLIST_PAGE_SIZE,
  ESortType,
} from '../api/core/resources/matchlist.resource';
import { ECompatibilityReportType } from '../api/models/member/member.types';
import { IInteractionsNormalizer } from '../api/repositories/interactions/interactions.normalizer';
import { compatibilityReportActions } from '../compatibilityReport/actions';
import { getCompatibilityReportSaga } from '../compatibilityReport/saga';
import { ActionsOfType } from '../helpers';
import { checkHasCompletedProfile } from '../helpers/checkSaga';
import { getPaginatedData } from '../helpers/pagination';
import { getMemberChoicesAction } from '../memberchoices/actions';
import { getProfileMemberChoicesSaga } from '../memberchoices/saga';
import {
  getCompatibilityReportLight,
  getMemberProfileAction,
} from '../members/actions';
import {
  getCompatibilityReportLightSaga,
  getMemberProfileSaga,
} from '../members/saga';
import {
  getPicturesAction,
  getPicturesSuccessAction,
} from '../pictures/actions';
import { getPicturesSaga } from '../pictures/saga';
import { EMatchesActionType, matchesActions, TMatchesActions } from './actions';

export function* getAffinyMatchListSaga() {
  if (!(yield* checkHasCompletedProfile())) return;

  const response = yield call(getMatchList);

  yield put(matchesActions.getAffinyMatchesSuccess(response));
  yield put(getPicturesSuccessAction(response));
}

export function* getAffinyAdditionalMatchListSaga(
  action: ActionsOfType<
    TMatchesActions,
    EMatchesActionType.GET_AFFINY_ADDITIONAL_MATCH_LIST
  >
) {
  if (!(yield* checkHasCompletedProfile())) return;

  const response = yield call(getMatchList, 1, {
    excludedMembers: action.payload.excludedMembers,
  });

  yield put(matchesActions.getAffinyAdditionalMatchesSuccess(response));
  yield put(getPicturesSuccessAction(response));
}

export function* getSortMatchListSaga(
  action: ActionsOfType<TMatchesActions, EMatchesActionType.GET_SORT_MATCH_LIST>
) {
  if (!(yield* checkHasCompletedProfile())) return;

  const sortType: ESortType = yield select(selectSortMatchListType);
  const response: IInteractionsNormalizer = yield call(
    getPaginatedData as any,
    {
      pageSelector: selectMatchListCurrentPage,
      totalSelector: selectMatchListTotal,
      pageSize: DEFAULT_MATCHLIST_PAGE_SIZE,
      reset: action.payload.reset,
      resetAction: matchesActions.resetMatchesList,
      apiCall: getMatchList,
      apiCallOptionalParameter: [{ sortType }],
      successAction: matchesActions.getMatchesSuccess,
    }
  );

  if (response) {
    yield put(getPicturesSuccessAction(response));
  }
}

export function* getMatchListUserSaga(
  action: ActionsOfType<
    TMatchesActions,
    EMatchesActionType.GET_USER_AFFINY_MATCHES
  >
) {
  yield all([
    call(getPicturesSaga, getPicturesAction(action.payload.accountId)),
    call(
      getProfileMemberChoicesSaga,
      getMemberChoicesAction({ accountId: action.payload.accountId })
    ),
    call(
      getMemberProfileSaga,
      getMemberProfileAction({ accountId: action.payload.accountId })
    ),
    call(
      getCompatibilityReportLightSaga,
      getCompatibilityReportLight({ accountId: action.payload.accountId })
    ),
    call(
      getCompatibilityReportSaga,
      compatibilityReportActions.getCompatibilityReport({
        accountId: action.payload.accountId,
        reportType: ECompatibilityReportType.VALUE,
      })
    ),
  ]);
}

export function* setSortTypeSaga(
  action: ActionsOfType<TMatchesActions, EMatchesActionType.SET_SORT_TYPE>
) {
  yield put(matchesActions.setSortTypeSuccess(action.payload.sortType));
  yield put(matchesActions.getMatches(true));
}

export function* matchesRootSaga() {
  yield all([
    takeLatest(
      EMatchesActionType.GET_AFFINY_MATCH_LIST,
      handleAsyncStatus(
        getAffinyMatchListSaga,
        EAsyncEventTypes.AFFINY_MATCH_LIST
      )
    ),
    takeLatest(
      EMatchesActionType.GET_AFFINY_ADDITIONAL_MATCH_LIST,
      handleAsyncStatus(
        getAffinyAdditionalMatchListSaga,
        EAsyncEventTypes.AFFINY_ADDITIONAL_MATCH_LIST
      )
    ),
    takeLatest(
      EMatchesActionType.GET_SORT_MATCH_LIST,
      handleAsyncStatus(getSortMatchListSaga, EAsyncEventTypes.SORT_MATCH_LIST)
    ),

    takeLatest(
      EMatchesActionType.GET_USER_AFFINY_MATCHES,
      getMatchListUserSaga
    ),
    takeLatest(
      EMatchesActionType.SET_SORT_TYPE,
      handleAsyncStatus(setSortTypeSaga, EAsyncEventTypes.SET_SORT_MATCH_LIST)
    ),
  ]);
}
